import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';
import { fetchImageAsBlob, blobToFile, isDataUrl, getImageFormatFromUrl } from './imageCompression';
import { PortfolioItem, PortfolioImage } from '@/hooks/useProfile';

export interface ConversionResult {
  success: boolean;
  convertedItems: PortfolioItem[];
  errors: string[];
  stats: {
    totalItems: number;
    convertedItems: number;
    totalImages: number;
    convertedImages: number;
    failedConversions: number;
  };
}

export interface ConversionOptions {
  bucketName?: string;
  folder?: string;
  maxRetries?: number;
  onProgress?: (progress: number, total: number) => void;
  onError?: (error: string) => void;
}

/**
 * Converts base64 images in portfolio items to proper storage URLs
 */
export const convertPortfolioBase64ToUrls = async (
  portfolioItems: PortfolioItem[],
  user: User,
  options: ConversionOptions = {}
): Promise<ConversionResult> => {
  const {
    bucketName = 'portfolio-images',
    folder = '',
    maxRetries = 3,
    onProgress,
    onError
  } = options;

  const result: ConversionResult = {
    success: true,
    convertedItems: [],
    errors: [],
    stats: {
      totalItems: portfolioItems.length,
      convertedItems: 0,
      totalImages: 0,
      convertedImages: 0,
      failedConversions: 0
    }
  };

  if (!user || !user.id) {
    result.success = false;
    result.errors.push('User authentication required');
    return result;
  }

  try {
    // Process each portfolio item
    for (let itemIndex = 0; itemIndex < portfolioItems.length; itemIndex++) {
      const item = portfolioItems[itemIndex];
      const convertedItem = { ...item };
      let itemModified = false;

      // Update progress
      onProgress?.(itemIndex, portfolioItems.length);

      // Check legacy image_url field
      if (item.image_url && isDataUrl(item.image_url)) {
        result.stats.totalImages++;
        
        try {
          const convertedUrl = await convertBase64ToStorageUrl(
            item.image_url,
            user,
            bucketName,
            folder,
            maxRetries
          );
          
          convertedItem.image_url = convertedUrl;
          itemModified = true;
          result.stats.convertedImages++;
        } catch (error) {
          const errorMsg = `Failed to convert image_url for item "${item.title}": ${error}`;
          result.errors.push(errorMsg);
          result.stats.failedConversions++;
          onError?.(errorMsg);
        }
      }

      // Check images array
      if (item.images && Array.isArray(item.images)) {
        const convertedImages: PortfolioImage[] = [];
        
        for (let imgIndex = 0; imgIndex < item.images.length; imgIndex++) {
          const image = item.images[imgIndex];
          
          if (image.url && isDataUrl(image.url)) {
            result.stats.totalImages++;
            
            try {
              const convertedUrl = await convertBase64ToStorageUrl(
                image.url,
                user,
                bucketName,
                folder,
                maxRetries
              );
              
              convertedImages.push({
                ...image,
                url: convertedUrl
              });
              
              itemModified = true;
              result.stats.convertedImages++;
            } catch (error) {
              const errorMsg = `Failed to convert image ${imgIndex + 1} for item "${item.title}": ${error}`;
              result.errors.push(errorMsg);
              result.stats.failedConversions++;
              onError?.(errorMsg);
              
              // Keep the original image even if conversion failed
              convertedImages.push(image);
            }
          } else {
            // Keep non-base64 images as they are
            convertedImages.push(image);
          }
        }
        
        if (itemModified) {
          convertedItem.images = convertedImages;
        }
      }

      // Add the item to results (whether modified or not)
      result.convertedItems.push(convertedItem);
      
      if (itemModified) {
        result.stats.convertedItems++;
      }
    }

    // Determine overall success
    result.success = result.stats.failedConversions === 0;
    
    return result;
  } catch (error) {
    result.success = false;
    result.errors.push(`Conversion process failed: ${error}`);
    return result;
  }
};

/**
 * Converts a single base64 image to a storage URL
 */
export const convertBase64ToStorageUrl = async (
  base64Url: string,
  user: User,
  bucketName: string = 'portfolio-images',
  folder: string = '',
  maxRetries: number = 3
): Promise<string> => {
  if (!isDataUrl(base64Url)) {
    throw new Error('URL is not a base64 data URL');
  }

  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Convert base64 to blob
      const blob = await fetchImageAsBlob(base64Url);
      
      // Determine file extension from base64 URL
      const format = getImageFormatFromUrl(base64Url) || 'jpg';
      const fileExt = format === 'jpeg' ? 'jpg' : format;
      
      // Generate unique filename
      const uniqueFileName = `${uuidv4()}.${fileExt}`;
      const filePath = folder ? `${folder}/${uniqueFileName}` : uniqueFileName;
      const fullPath = `${user.id}/${filePath}`;
      
      // Create a File object from the blob
      const file = blobToFile(blob, uniqueFileName);
      
      // Upload to storage
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(fullPath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        });

      if (error) {
        throw error;
      }

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucketName)
        .getPublicUrl(fullPath);

      return publicUrl;
    } catch (error) {
      lastError = error as Error;
      console.warn(`Attempt ${attempt}/${maxRetries} failed:`, error);
      
      if (attempt < maxRetries) {
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, attempt * 1000));
      }
    }
  }

  throw new Error(`Failed to convert base64 image after ${maxRetries} attempts: ${lastError?.message}`);
};

/**
 * Checks if a portfolio item contains base64 images
 */
export const hasBase64Images = (item: PortfolioItem): boolean => {
  // Check legacy image_url
  if (item.image_url && isDataUrl(item.image_url)) {
    return true;
  }
  
  // Check images array
  if (item.images && Array.isArray(item.images)) {
    return item.images.some(image => image.url && isDataUrl(image.url));
  }
  
  return false;
};

/**
 * Counts the number of base64 images in portfolio items
 */
export const countBase64Images = (portfolioItems: PortfolioItem[]): number => {
  let count = 0;
  
  for (const item of portfolioItems) {
    // Check legacy image_url
    if (item.image_url && isDataUrl(item.image_url)) {
      count++;
    }
    
    // Check images array
    if (item.images && Array.isArray(item.images)) {
      count += item.images.filter(image => image.url && isDataUrl(image.url)).length;
    }
  }
  
  return count;
};

/**
 * Validates that a portfolio item has been properly converted
 */
export const validateConversion = (item: PortfolioItem): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Check legacy image_url
  if (item.image_url && isDataUrl(item.image_url)) {
    errors.push('Legacy image_url still contains base64 data');
  }
  
  // Check images array
  if (item.images && Array.isArray(item.images)) {
    item.images.forEach((image, index) => {
      if (image.url && isDataUrl(image.url)) {
        errors.push(`Image ${index + 1} still contains base64 data`);
      }
    });
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}; 